import { useState, useEffect } from "react";
import { Loader2, ArrowUpRight, ArrowDownRight } from "lucide-react";
import Chart from "react-apexcharts";
import { InfoTooltip, GraphExplainer, DataSourceBadge, CHART_3D } from "./DashboardWidgets";
import api from "../../utils/api";

function fmt(n) { return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n); }
function fmtK(n) { return n >= 1000 ? `$${(n/1000).toFixed(0)}k` : `$${n}`; }
const CARD = { border: "1px solid #E8EAF0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" };

export default function SalesTab({ dateRange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (dateRange?.start) params.set("start_date", dateRange.start);
    if (dateRange?.end) params.set("end_date", dateRange.end);
    api.get(`/dashboard-data/sales-dashboard?${params.toString()}`).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [dateRange]);

  if (loading || !data) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" style={{ color: "#1B75BA" }} /></div>;
  const { kpis, top_customers, sales_vs_profit: svp, sales_by_customer: sbc, yoy_comparison: yoy, hubspot_live, data_range, last_sync } = data;

  return (
    <div className="space-y-3" data-testid="sales-tab">
      {hubspot_live && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: "#EFF6FF", border: "1px solid #BFDBFE" }}>
          <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: "#1B75BA" }} />
          <span className="text-xs font-semibold" style={{ color: "#1B75BA" }}>Live Data - Powered by your HubSpot CRM</span>
          {data_range && (
            <span className="text-[10px] ml-auto" style={{ color: "#6B7491" }}>
              Data range: {data_range.start} to {data_range.end}
              {last_sync && <> | Last synced: {new Date(last_sync).toLocaleTimeString()}</>}
            </span>
          )}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        {kpis.map((kpi, i) => (
          <div key={i} className="bg-white rounded-xl p-3" style={CARD} data-testid={`sales-kpi-${i}`}>
            <div className="flex items-center justify-between mb-0.5">
              <p className="text-[9px] font-medium" style={{ color: "#6B7491" }}>{kpi.label}</p>
              <DataSourceBadge source={hubspot_live ? "hubspot" : (i < 3 ? "hubspot" : "quickbooks")} />
            </div>
            <p className="text-lg font-bold font-display mt-0.5" style={{ color: "#1C2040" }}>{kpi.value}</p>
            {kpi.change !== null && !kpi.risk && (
              <div className="flex items-center gap-0.5 mt-0.5">
                {kpi.positive ? <ArrowUpRight className="w-3 h-3" style={{ color: "#3B82F6" }} /> : <ArrowDownRight className="w-3 h-3" style={{ color: "#F5841F" }} />}
                <span className="text-[9px] font-semibold font-mono" style={{ color: kpi.positive ? "#3B82F6" : "#F5841F" }}>+{kpi.change}%</span>
              </div>
            )}
            {kpi.risk && (
              <div className="flex items-center gap-0.5 mt-0.5">
                <ArrowDownRight className="w-3 h-3" style={{ color: "#F5841F" }} />
                <span className="text-[9px] font-semibold" style={{ color: "#F5841F" }}>{kpi.risk}</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Row 2: Top 5 Customers + Sales vs Profit */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="bg-white rounded-xl p-4" style={CARD} data-testid="sales-top-customers">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold font-display" style={{ color: "#1C2040" }}>Sales By Customer - Top 5</h3>
              <DataSourceBadge source="hubspot" />
            </div>
            <GraphExplainer title="Top 5 Customers" text="Horizontal bars show your highest-revenue customers. Longer bars = more revenue. Focus on retaining these key accounts. If one customer dominates, consider diversifying to reduce concentration risk." />
          </div>
          <Chart type="bar" height={240} options={{
            ...CHART_3D,
            plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "60%" } },
            xaxis: { labels: { formatter: v => fmtK(v), style: { fontSize: "10px", colors: "#9BA4B8" } } },
            yaxis: { labels: { style: { fontSize: "11px", colors: "#3A3F5C" } } },
            colors: ["#D4A017"],
            tooltip: { y: { formatter: v => fmt(v) } },
          }} series={[{ name: "Amount", data: top_customers.map(c => ({ x: c.name, y: c.amount })) }]} />
        </div>

        <div className="bg-white rounded-xl p-4" style={CARD} data-testid="sales-vs-profit">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold font-display" style={{ color: "#1C2040" }}>Sales vs Profit Benchmark</h3>
              <DataSourceBadge source="quickbooks" />
            </div>
            <GraphExplainer title="Sales vs Profit" text="Side-by-side comparison of total sales (gold) and profit (purple) each month. The gap between them represents your operating costs. A widening gap means expenses are growing faster than sales." />
          </div>
          <Chart type="bar" height={240} options={{
            ...CHART_3D,
            xaxis: { categories: svp.months, labels: { style: { fontSize: "11px", colors: "#9BA4B8" } } },
            yaxis: { labels: { formatter: v => fmtK(v), style: { fontSize: "11px", colors: "#9BA4B8" } } },
            colors: ["#8B5CF6", "#D4A017"],
            plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
            legend: { show: true, position: "top", fontSize: "10px" },
          }} series={[
            { name: "Profit", data: svp.profit },
            { name: "Sales", data: svp.sales },
          ]} />
        </div>
      </div>

      {/* Row 3: Sales by Customer Line + YOY */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="bg-white rounded-xl p-4" style={CARD} data-testid="sales-by-customer-line">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold font-display" style={{ color: "#1C2040" }}>Sales By Customer</h3>
              <DataSourceBadge source="hubspot" />
            </div>
            <GraphExplainer title="Sales By Customer Trend" text="Line chart showing sales and profit trends over time. Purple = profit, Gold = sales. Converging lines indicate improving margins. Diverging lines suggest rising costs against stable revenue." />
          </div>
          <Chart type="line" height={240} options={{
            ...CHART_3D,
            xaxis: { categories: sbc.months, labels: { style: { fontSize: "10px", colors: "#9BA4B8" } } },
            yaxis: { labels: { style: { fontSize: "11px", colors: "#9BA4B8" } } },
            colors: ["#8B5CF6", "#D4A017"],
            stroke: { curve: "smooth", width: 2.5 },
            fill: { type: "solid", opacity: 1 },
            legend: { show: true, position: "top", fontSize: "10px" },
            markers: { size: 4, strokeWidth: 0 },
          }} series={[
            { name: "Profit", data: sbc.profit },
            { name: "Sales", data: sbc.sales },
          ]} />
        </div>

        <div className="bg-white rounded-xl p-4" style={CARD} data-testid="sales-yoy">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold font-display" style={{ color: "#1C2040" }}>Sales Comparison YOY</h3>
              <DataSourceBadge source="quickbooks" />
            </div>
            <GraphExplainer title="Year-over-Year Sales" text="Monthly sales compared across 3 years. Purple = 2023, Amber = 2024, Blue = 2025. Consistent growth across all months indicates healthy expansion. Seasonal dips help with demand forecasting." />
          </div>
          <Chart type="bar" height={240} options={{
            ...CHART_3D,
            xaxis: { categories: yoy.months, labels: { style: { fontSize: "11px", colors: "#9BA4B8" } } },
            yaxis: { labels: { formatter: v => fmtK(v), style: { fontSize: "11px", colors: "#9BA4B8" } } },
            colors: ["#8B5CF6", "#F59E0B", "#1B75BA"],
            plotOptions: { bar: { borderRadius: 4, columnWidth: "65%" } },
            legend: { show: true, position: "top", fontSize: "10px" },
          }} series={[
            { name: "2023", data: yoy.y2023 },
            { name: "2024", data: yoy.y2024 },
            { name: "2025", data: yoy.y2025 },
          ]} />
        </div>
      </div>
    </div>
  );
}
